import React, { useMemo, useState } from 'react';
import toast from '../../utils/toast';
import { 
  Activity, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle, 
  RefreshCw,
  Server,
  Eye,
  Shield,
  Bell,
  X,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  ExternalLink
} from 'lucide-react';
import { monitoringAPI } from '../../services/api';
import { formatDateTime, formatRelativeTime } from '../../utils/dateUtils';
import { formatLeaveDays } from '../../utils/numberUtils';
import { useMonitoring } from '../../contexts/MonitoringContext';
import { useAuditLogs } from '../../hooks/useMonitoringData';
import { useTabPersistence } from '../../hooks/usePersistence';
import Portal from '../../components/UI/Portal';
import { StatusBadge } from '../../components/UI/StatusBadge';
import { Pagination } from '../../components/UI/Pagination';
import { CountBadge } from '../../components/UI/CountBadge';
import { FilterButton } from '../../components/UI/FilterButton';
import DebouncedSearchInput from '../../components/UI/DebouncedSearchInput';
import IconButton from '../../components/UI/IconButton';

const VALID_TABS = ['overview', 'tasks', 'health', 'alerts', 'audit'];

/**
 * Mobile card list for per-employee task-effect rows (md:hidden counterpart
 * to the desktop effects tables in the task details modal).
 */
const EffectsMobileCards = ({ rows, fields, renderBadge }) => (
  <div className="md:hidden p-4 space-y-3">
    {rows.map((row, idx) => (
      <div key={idx} className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{row.staff_name || '-'}</p>
            <p className="text-xs text-gray-500">{row.staff_id_display || row.staff_id || '-'}</p>
          </div>
          {renderBadge && (
            <div className="flex-shrink-0">{renderBadge(row)}</div>
          )}
        </div>
        <div className="mt-3 space-y-2">
          {fields.map((field) => (
            <div key={field.label} className="flex items-start justify-between gap-3 text-sm">
              <span className="text-gray-500 flex-shrink-0">{field.label}</span>
              <span className={`text-gray-900 text-right truncate min-w-0${field.numeric ? ' tabular-nums' : ''}`}>
                {field.render(row)}
              </span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const SystemMonitoring = () => {
  const {
    taskExecutions,
    systemHealth,
    systemAlerts,
    loading,
    isRefreshing,
    errors,
    lastUpdated,
    loadData,
    metrics
  } = useMonitoring();

  const [activeTab, setActiveTab] = useTabPersistence(
    'system-monitoring',
    'overview',
    VALID_TABS
  );
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskEffects, setTaskEffects] = useState({ loading: false, rows: [], count: 0 });
  
  // Tasks tab filtering state
  const [taskSearch, setTaskSearch] = useState('');
  const [showTaskFilters, setShowTaskFilters] = useState(false);
  const [taskFilters, setTaskFilters] = useState({
    taskStatus: '',
    taskName: '',
    startDate: '',
    endDate: ''
  });

  // Filtered tasks based on filters
  const filteredTaskExecutions = useMemo(() => {
    let filtered = [...taskExecutions];

    // Search filter
    if (taskSearch) {
      const searchLower = taskSearch.toLowerCase();
      filtered = filtered.filter(task => 
        (task.task_name || '').toLowerCase().includes(searchLower) ||
        (task.task_display_name || '').toLowerCase().includes(searchLower) ||
        (task.task_id || '').toLowerCase().includes(searchLower) ||
        (task.result_summary || '').toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (taskFilters.taskStatus) {
      filtered = filtered.filter(task => task.task_status === taskFilters.taskStatus);
    }

    // Task name filter
    if (taskFilters.taskName) {
      filtered = filtered.filter(task => 
        (task.task_name || '').includes(taskFilters.taskName)
      );
    }

    // Date range filter
    if (taskFilters.startDate) {
      const startDate = new Date(taskFilters.startDate);
      filtered = filtered.filter(task => {
        if (!task.started_at) return false;
        return new Date(task.started_at) >= startDate;
      });
    }
    if (taskFilters.endDate) {
      const endDate = new Date(taskFilters.endDate);
      endDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(task => {
        if (!task.started_at) return false;
        return new Date(task.started_at) <= endDate;
      });
    }

    return filtered;
  }, [taskExecutions, taskSearch, taskFilters]);
  
  // Audit tab state variables
  const [auditSearch, setAuditSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [auditFilters, setAuditFilters] = useState({
    staffId: '',
    actionType: '',
    modelName: '',
    startDate: '',
    endDate: ''
  });
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(20);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);

  const auditParams = useMemo(() => {
    const params = {
      page: auditPage,
      page_size: auditPageSize,
      ordering: '-audit_timestamp'
    };
    if (auditSearch) params.search = auditSearch;
    if (auditFilters.staffId) params.audit_staff_id__staff_id = auditFilters.staffId;
    if (auditFilters.actionType) params.audit_action_type = auditFilters.actionType;
    if (auditFilters.modelName) params.audit_model_name = auditFilters.modelName;
    if (auditFilters.startDate) params.audit_timestamp__gte = auditFilters.startDate;
    if (auditFilters.endDate) params.audit_timestamp__lte = auditFilters.endDate;
    return params;
  }, [auditFilters, auditPage, auditPageSize, auditSearch]);

  const {
    data: auditData,
    isError: auditError,
    isFetching: auditRefreshing,
    isPending: auditLoading,
    refetch: refetchAuditLogs,
  } = useAuditLogs(auditParams, activeTab === 'audit');
  const auditLogs = auditData?.rows || [];
  const auditCount = auditData?.count || 0;

  const tabs = [
    { 
      id: 'overview', 
      name: 'Overview', 
      icon: Activity, 
      description: 'System health at a glance'
    },
    { 
      id: 'tasks', 
      name: 'Tasks', 
      icon: Clock, 
      description: 'Monitor scheduled task executions'
    },
    { 
      id: 'health', 
      name: 'Health', 
      icon: Server, 
      description: 'Service status and performance',
      badge: systemHealth.filter(h => h.status === 'DOWN' || h.status === 'CRITICAL').length || null
    },
    { 
      id: 'alerts', 
      name: 'Alerts', 
      icon: Bell, 
      description: 'Issues requiring attention',
      badge: systemAlerts.filter(alert => !alert.resolved).length || null
    },
    { 
      id: 'audit', 
      name: 'Audit', 
      icon: Shield, 
      description: 'User activity and data changes'
    }
  ];

  const handleRefresh = async () => {
    await loadData();
    if (activeTab === 'audit') {
      const result = await refetchAuditLogs();
      if (result.isError) {
        toast.warning('Audit logs could not be refreshed');
      }
    }
  };

  const openTaskDetails = async (task) => {
    setSelectedTask(task);
    setShowTaskModal(true);
    setTaskEffects(prev => ({ ...prev, loading: true }));
    try {
      const id = task.id;
      const res = await monitoringAPI.getTaskEffects(id, { page_size: 200 });
      
      // Handle both paginated response format ({count, results}) and direct array response format
      let rows = [];
      let count = 0;
      
      if (res?.data) {
        // Check if response is paginated (has 'results' and 'count')
        if (Array.isArray(res.data.results) && typeof res.data.count === 'number') {
          rows = res.data.results;
          count = res.data.count;
        } 
        // Check if response is a direct array
        else if (Array.isArray(res.data)) {
          rows = res.data;
          count = res.data.length;
        }
        // Fallback: check for nested data structure
        else if (res.data.data && Array.isArray(res.data.data)) {
          rows = res.data.data;
          count = res.data.data.length;
        }
      }
      
      // Fallback to truncated effects stored in result_data if API has nothing
      if ((!rows || rows.length === 0) && task?.result_data?.effects_truncated) {
        rows = task.result_data.effects_truncated.map((r) => ({
          staff_id_display: r.staff_id,
          staff_name: r.staff_name,
          before_accrued_days: r.before_accrued_days,
          delta_accrued_days: r.delta_accrued_days,
          after_accrued_days: r.after_accrued_days,
          before_remaining_days: r.before_remaining_days,
          after_remaining_days: r.after_remaining_days,
        }));
        count = task.result_data.effects_count || rows.length;
      }
      
      setTaskEffects({ loading: false, rows, count });
    } catch (error) {
      toast.error('Failed to load task effects. Please try again.');
      setTaskEffects({ loading: false, rows: [], count: 0 });
    }
  };

  const closeTaskDetails = () => {
    setShowTaskModal(false);
    setSelectedTask(null);
    setTaskEffects({ loading: false, rows: [], count: 0 });
  };

  const navigateAuditLog = (direction) => {
    const currentIndex = auditLogs.findIndex(log => log === selectedAuditLog);
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedAuditLog(auditLogs[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < auditLogs.length - 1) {
      setSelectedAuditLog(auditLogs[currentIndex + 1]);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'failure':
      case 'down':
      case 'critical':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'started':
      case 'pending':
        return <Clock className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTaskStatusForBadge = (status) => {
    switch (status?.toUpperCase()) {
      case 'SUCCESS':
        return { status: 'completed', label: 'Success' };
      case 'FAILURE':
        return { status: 'failed', label: 'Failure' };
      case 'PENDING':
        return { status: 'pending' };
      case 'STARTED':
        return { status: 'in_progress', label: 'Started' };
      case 'RETRY':
        return { status: 'pending', label: 'Retry' };
      default:
        return { status: status?.toLowerCase() || 'draft' };
    }
  };

  const getHealthStatusForBadge = (status) => {
    const s = status?.toUpperCase();
    if (s === 'UP' || s === 'HEALTHY') return { status: 'active', label: s === 'UP' ? 'Up' : 'Healthy' };
    if (s === 'DOWN' || s === 'CRITICAL') return { status: 'failed', label: s === 'DOWN' ? 'Down' : 'Critical' };
    if (s === 'WARNING') return { status: 'on_hold', label: 'Warning' };
    return { status: status?.toLowerCase() || 'draft' };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const AuditDetailsModal = ({ isOpen, auditLog, onClose, onPrevious, onNext }) => {
    if (!isOpen || !auditLog) return null;
    
    const parseUserAgent = (ua) => {
      if (!ua) return 'Unknown';
      if (ua.includes('Chrome')) return 'Chrome';
      if (ua.includes('Firefox')) return 'Firefox';
      if (ua.includes('Safari')) return 'Safari';
      return 'Unknown Browser';
    };
    
    return (
      <Portal>
        <div className="modal-overlay">
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={onClose}
          />
          
          <div className="flex items-center justify-center min-h-screen p-4">
            <div className="modal-container modal-container-lg">
              {/* Header */}
              <div className="modal-header">
                <div>
                  <h3 className="modal-title">Audit Log Details</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDateTime(auditLog.audit_timestamp)}
                  </p>
                </div>
                <button onClick={onClose} className="modal-close-admin">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Content */}
              <div className="modal-content space-y-6">
                {/* Action Badge */}
                <div>
                  <StatusBadge
                    status={
                      auditLog.audit_action_type === 'CREATE' ? 'approved' :
                      auditLog.audit_action_type === 'UPDATE' ? 'in_progress' :
                      auditLog.audit_action_type === 'DELETE' ? 'rejected' :
                      'draft'
                    }
                    label={auditLog.audit_action_type}
                  />
                </div>
                
                {/* Key Information Grid - Industry Standard Pattern */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Action Source - Who/What triggered the change */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">Action Source</label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${
                        !auditLog.audit_user_agent ? 'bg-purple-100' : 'bg-blue-100'
                      }`}>
                        {!auditLog.audit_user_agent ? (
                          <span className="text-xs">🤖</span>
                        ) : (
                          <span className="text-xs">👤</span>
                        )}
                      </span>
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {!auditLog.audit_user_agent ? 'System (Automated Task)' : (auditLog.audit_staff_id || 'Unknown User')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {!auditLog.audit_user_agent ? 'Automated' : 'Manual'}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Affected Employee - Who this change is about */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">Affected Employee</label>
                    <div className="mt-1">
                      {auditLog.audit_staff_id ? (
                        <div className="text-sm text-gray-900">
                          <span className="font-medium">{auditLog.audit_staff_id}</span>
                          <div className="text-xs text-gray-500">Staff Member</div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Model */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">Model</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {auditLog.audit_model_name}
                    </div>
                  </div>
                  
                  {/* Record ID */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">Record ID</label>
                    <div className="mt-1">
                      {auditLog.audit_record_id ? (
                        <span className="text-sm text-gray-900 font-mono">
                          {auditLog.audit_record_id}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </div>
                  </div>
                  
                  {/* IP Address */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">IP Address</label>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm text-gray-900 font-mono">
                        {auditLog.audit_ip_address || 'N/A'}
                      </span>
                      {auditLog.audit_ip_address && 
                       !auditLog.audit_ip_address.startsWith('192.168') && 
                       !auditLog.audit_ip_address.startsWith('127.0') && (
                        <StatusBadge status="on_hold" label="External" />
                      )}
                    </div>
                  </div>
                  
                  {/* Device/Browser */}
                  <div>
                    <label className="text-xs font-medium text-gray-600">Device/Browser</label>
                    <div className="mt-1 text-sm text-gray-900">
                      {auditLog.audit_user_agent ? parseUserAgent(auditLog.audit_user_agent) : 'Server-side'}
                    </div>
                  </div>
                </div>
                
                {/* Full User Agent (collapsible) - Move outside grid */}
                {auditLog.audit_user_agent && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                      Show full user agent string
                    </summary>
                    <pre className="mt-2 p-3 bg-gray-50 rounded-lg text-xs overflow-x-auto">
                      {auditLog.audit_user_agent}
                    </pre>
                  </details>
                )}
                
                {/* Change Details */}
                <div>
                  <label className="text-xs font-medium text-gray-600">Change Details</label>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <pre className="text-sm text-gray-900 whitespace-pre-wrap">
                      {auditLog.audit_details || 'No details available'}
                    </pre>
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="modal-footer">
                <div className="flex gap-2">
                  <button
                    onClick={onPrevious}
                    className="btn btn-admin-outline"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Previous
                  </button>
                  <button
                    onClick={onNext}
                    className="btn btn-admin-outline"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </button>
                </div>
                <button
                  onClick={onClose}
                  className="btn btn-admin-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </Portal>
    );
  };

  const renderOverview = () => {
    const recentTasks = taskExecutions.slice(0, 5);
    const recentHealth = systemHealth.slice(0, 3);
    const criticalAlerts = systemAlerts.filter(
      alert => !alert.resolved && alert.severity?.toLowerCase() === 'critical'
    );
    const failedTasks24h = taskExecutions.filter(t => {
      if (t.task_status !== 'FAILURE' || !t.completed_at) return false;
      const failureTime = new Date(t.completed_at);
      const hoursSince = (Date.now() - failureTime.getTime()) / (1000 * 60 * 60);
      return hoursSince <= 24;
    });

    return (
      <div className="space-y-8">
        {/* Smart Metrics Cards - Enhanced with Critical Issues */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6">
          {/* Critical Issues - NEW */}
          <div className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 ${
            metrics.criticalAlerts > 0 ? 'border-red-300 bg-red-50' : ''
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  metrics.criticalAlerts > 0 ? 'bg-red-200' : 'bg-red-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    metrics.criticalAlerts > 0 ? 'text-red-700' : 'text-red-600'
                  }`} />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className={`text-lg sm:text-2xl font-bold ${
                    metrics.criticalAlerts > 0 ? 'text-red-900' : 'text-gray-900'
                  }`}>
                    {metrics.criticalAlerts}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Critical Issues</p>
                </div>
              </div>
              {metrics.criticalAlerts > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('alerts');
                    toast.success('Showing critical alerts');
                  }}
                  className="text-red-600 hover:text-red-700"
                  title="View critical alerts"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Services Down - NEW */}
          <div className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 ${
            metrics.servicesDown > 0 ? 'border-orange-300 bg-orange-50' : ''
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  metrics.servicesDown > 0 ? 'bg-orange-200' : 'bg-orange-100'
                }`}>
                  <Server className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    metrics.servicesDown > 0 ? 'text-orange-700' : 'text-orange-600'
                  }`} />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className={`text-lg sm:text-2xl font-bold ${
                    metrics.servicesDown > 0 ? 'text-orange-900' : 'text-gray-900'
                  }`}>
                    {metrics.servicesDown}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Services Down</p>
                </div>
              </div>
              {metrics.servicesDown > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('health');
                    toast.success('Showing health status');
                  }}
                  className="text-orange-600 hover:text-orange-700"
                  title="View health status"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Recent Failures (24h) - NEW */}
          <div className={`bg-white rounded-2xl shadow-sm border p-4 sm:p-6 ${
            metrics.recentFailures > 0 ? 'border-yellow-300 bg-yellow-50' : ''
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  metrics.recentFailures > 0 ? 'bg-yellow-200' : 'bg-yellow-100'
                }`}>
                  <XCircle className={`w-5 h-5 sm:w-6 sm:h-6 ${
                    metrics.recentFailures > 0 ? 'text-yellow-700' : 'text-yellow-600'
                  }`} />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className={`text-lg sm:text-2xl font-bold ${
                    metrics.recentFailures > 0 ? 'text-yellow-900' : 'text-gray-900'
                  }`}>
                    {metrics.recentFailures}
                  </p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Failures (24h)</p>
                </div>
              </div>
              {metrics.recentFailures > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('tasks');
                    toast.success('Showing failed tasks');
                  }}
                  className="text-yellow-600 hover:text-yellow-700"
                  title="View failed tasks"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
          
          {/* Active Alerts */}
          <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0">
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bell className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-lg sm:text-2xl font-bold text-gray-900">{metrics.activeAlerts}</p>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Active Alerts</p>
                </div>
              </div>
              {metrics.activeAlerts > 0 && (
                <button
                  onClick={() => setActiveTab('alerts')}
                  className="text-gray-600 hover:text-gray-900"
                  title="View all alerts"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Section - NEW */}
        {(criticalAlerts.length > 0 || metrics.servicesDown > 0 || failedTasks24h.length > 0) && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl shadow-sm border border-red-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Quick Actions</h3>
                <p className="text-sm text-gray-600 mt-1">Issues requiring immediate attention</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {criticalAlerts.length > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('alerts');
                    toast.success('Showing critical alerts');
                  }}
                  className="bg-white rounded-xl p-6 border border-red-200 hover:border-red-300 hover:shadow-md transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{criticalAlerts.length} Critical Alert{criticalAlerts.length !== 1 ? 's' : ''}</p>
                      <p className="text-xs text-gray-500 mt-1">Requires immediate attention</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-red-600" />
                  </div>
                </button>
              )}
              {metrics.servicesDown > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('health');
                    toast.success('Showing health status');
                  }}
                  className="bg-white rounded-xl p-6 border border-orange-200 hover:border-orange-300 hover:shadow-md transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{metrics.servicesDown} Service{metrics.servicesDown !== 1 ? 's' : ''} Down</p>
                      <p className="text-xs text-gray-500 mt-1">Check system health</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-orange-600" />
                  </div>
                </button>
              )}
              {failedTasks24h.length > 0 && (
                <button
                  onClick={() => {
                    setActiveTab('tasks');
                    toast.success('Showing failed tasks');
                  }}
                  className="bg-white rounded-xl p-6 border border-yellow-200 hover:border-yellow-300 hover:shadow-md transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{failedTasks24h.length} Failed Task{failedTasks24h.length !== 1 ? 's' : ''}</p>
                      <p className="text-xs text-gray-500 mt-1">In last 24 hours</p>
                    </div>
                    <ExternalLink className="w-5 h-5 text-yellow-600" />
                  </div>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Recent Tasks */}
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h3 className="text-2xl font-bold text-gray-900">Recent Task Executions</h3>
            <p className="text-sm text-gray-500 mt-1">Latest automated task activity</p>
          </div>
          <div className="p-6">
            {recentTasks.length > 0 ? (
              <div className="space-y-4">
                {recentTasks.map((task) => (
                  <div
                    key={task.id || task.task_id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200 cursor-pointer"
                    onClick={() => openTaskDetails(task)}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm">
                        {getStatusIcon(task.task_status)}
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900">
                          {task.task_display_name || task.task_name?.replace('staff_core.tasks.', '').replace(/_/g, ' ')}
                        </p>
                        <p className="text-xs text-gray-500">{formatDateTime(task.started_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{formatDuration(task.duration_seconds)}</p>
                      <p className="text-xs text-gray-500 max-w-xs truncate">{task.result_summary || 'No summary'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900 mb-1">No Recent Tasks</p>
                <p className="text-sm text-gray-500">Task executions will appear here once available</p>
              </div>
            )}
          </div>
        </div>

        {/* System Health */}
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="px-6 py-4 border-b">
            <h3 className="text-2xl font-bold text-gray-900">System Health Status</h3>
            <p className="text-sm text-gray-500 mt-1">Current service status and performance</p>
          </div>
          <div className="p-6">
            {recentHealth.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {recentHealth.map((health) => (
                  <div key={health.service_name} className="p-4 bg-gray-50 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
                        {getStatusIcon(health.status)}
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900">
                          {health.service_display_name || health.service_name}
                        </p>
                        <p className="text-xs text-gray-500">{health.response_time_ms ? `${health.response_time_ms}ms` : 'N/A'}</p>
                      </div>
                    </div>
                    <StatusBadge {...getHealthStatusForBadge(health.status)} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Server className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No health check data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderTasks = () => (
    <div className="bg-white rounded-2xl shadow-sm border">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">Automated Task Executions</h3>
        <p className="text-sm text-gray-500 mt-1">Monitor your scheduled HR tasks</p>
      </div>

      {/* Toolbar with Search and Filters */}
      <div className="px-6 py-4 border-b">
        <div className="flex items-center justify-between gap-4">
          <DebouncedSearchInput
            value={taskSearch}
            onSearch={setTaskSearch}
            placeholder="Search tasks by name, ID, or result..."
            className="max-w-xl"
          />
          <FilterButton
            theme="admin"
            isOpen={showTaskFilters}
            activeCount={[taskFilters.taskStatus, taskFilters.taskName, taskFilters.startDate, taskFilters.endDate].filter(Boolean).length}
            onClick={() => setShowTaskFilters(v => !v)}
          />
        </div>

        {/* Filter Panel */}
        {showTaskFilters && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-group">
                <label className="form-label text-xs text-gray-600">Status</label>
                <select
                  className="select-admin w-full"
                  value={taskFilters.taskStatus}
                  onChange={(e) => setTaskFilters({...taskFilters, taskStatus: e.target.value})}
                >
                  <option value="">All Statuses</option>
                  <option value="SUCCESS">Success</option>
                  <option value="FAILURE">Failure</option>
                  <option value="STARTED">Started</option>
                  <option value="PENDING">Pending</option>
                  <option value="RETRY">Retry</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label text-xs text-gray-600">Task Name</label>
                <select
                  className="select-admin w-full"
                  value={taskFilters.taskName}
                  onChange={(e) => setTaskFilters({...taskFilters, taskName: e.target.value})}
                >
                  <option value="">All Tasks</option>
                  <option value="process_annual_leave_accrual">Process Annual Leave Accrual</option>
                  <option value="materialize_service_gated_balances">Materialize Service Gated Balances</option>
                  <option value="update_leave_balances">Update Leave Balances</option>
                  <option value="sync_singapore_holidays_task">Sync Singapore Holidays</option>
                  <option value="sync_cpf_rates_task">Sync CPF Rates</option>
                </select>
              </div>

              <div className="form-group lg:col-span-2">
                <label className="form-label text-xs text-gray-600">Date Range</label>
                <div className="flex gap-2">
                  <input
                    type="date"
                    className="input-admin w-full"
                    value={taskFilters.startDate}
                    onChange={(e) => setTaskFilters({...taskFilters, startDate: e.target.value})}
                    placeholder="Start date"
                  />
                  <input
                    type="date"
                    className="input-admin w-full"
                    value={taskFilters.endDate}
                    onChange={(e) => setTaskFilters({...taskFilters, endDate: e.target.value})}
                    placeholder="End date"
                  />
                </div>
              </div>

              <div className="form-group flex items-end md:col-span-2 lg:col-span-1 ml-4">
                <button
                  onClick={() => {
                    setTaskSearch('');
                    setTaskFilters({taskStatus: '', taskName: '', startDate: '', endDate: ''});
                  }}
                  className="btn btn-outline btn-sm w-full"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Active Filter Chips */}
        {(taskSearch || taskFilters.taskStatus || taskFilters.taskName || taskFilters.startDate || taskFilters.endDate) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {taskSearch && (
              <span className="pill pill-secondary inline-flex items-center">
                Search: {taskSearch}
                <button onClick={() => setTaskSearch('')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {taskFilters.taskStatus && (
              <span className="pill pill-secondary inline-flex items-center">
                Status: {taskFilters.taskStatus}
                <button onClick={() => setTaskFilters({...taskFilters, taskStatus: ''})} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {taskFilters.taskName && (
              <span className="pill pill-secondary inline-flex items-center">
                Task: {taskFilters.taskName.replace('staff_core.tasks.', '').replace(/_/g, ' ')}
                <button onClick={() => setTaskFilters({...taskFilters, taskName: ''})} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {taskFilters.startDate && (
              <span className="pill pill-secondary inline-flex items-center">
                From: {taskFilters.startDate}
                <button onClick={() => setTaskFilters({...taskFilters, startDate: ''})} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {taskFilters.endDate && (
              <span className="pill pill-secondary inline-flex items-center">
                To: {taskFilters.endDate}
                <button onClick={() => setTaskFilters({...taskFilters, endDate: ''})} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}

        {/* Results count */}
        <div className="mt-2 text-xs text-gray-500">
          {taskSearch || taskFilters.taskStatus || taskFilters.taskName || taskFilters.startDate || taskFilters.endDate
            ? `Showing ${filteredTaskExecutions.length} matches within the latest ${taskExecutions.length} of ${metrics.totalTasks} executions`
            : `Showing the latest ${taskExecutions.length} of ${metrics.totalTasks} task executions`}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Task</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Started</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Result</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredTaskExecutions.length > 0 ? (
              filteredTaskExecutions.map((task) => (
              <tr key={task.id || task.task_id} className="table-row cursor-pointer min-h-[44px]" onClick={() => openTaskDetails(task)}>
                <td className="px-6 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <Clock className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {task.task_display_name || task.task_name?.replace('staff_core.tasks.', '').replace(/_/g, ' ')}
                      </div>
                      <div className="text-xs text-gray-500">{task.task_id}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(task.task_status)}
                    <StatusBadge {...getTaskStatusForBadge(task.task_status)} />
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-gray-900">
                  {formatDateTime(task.started_at)}
                </td>
                <td className="px-6 py-3 text-sm text-gray-900">
                  {formatDuration(task.duration_seconds)}
                </td>
                <td className="px-6 py-3">
                  <div className="flex items-center justify-between">
                    <div className="max-w-xs truncate text-sm text-gray-900">
                      {task.result_summary || 'No summary available'}
                    </div>
                    {task.related_alert_count > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveTab('alerts');
                          toast.success(`Showing ${task.related_alert_count} related alert(s)`);
                        }}
                        className="ml-2 inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors gap-1"
                        title={`View ${task.related_alert_count} related alert(s)`}
                      >
                        <Bell className="w-3 h-3" />
                        <span>{task.related_alert_count}</span>
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-lg font-semibold text-gray-900 mb-1">No Tasks Found</p>
                  <p className="text-sm text-gray-500">
                    {errors.tasks
                      ? 'Task executions are unavailable. The portal will retry automatically.'
                      : taskExecutions.length === 0
                        ? 'No task executions available'
                      : 'No tasks match your current filters. Try adjusting your search or filters.'}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderHealth = () => (
    <div className="bg-white rounded-2xl shadow-sm border">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">System Health Checks</h3>
        <p className="text-sm text-gray-500 mt-1">Monitor system services and performance</p>
      </div>
      <div className="p-6">
        {systemHealth.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {systemHealth.map((health) => (
              <div key={health.service_name} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center justify-between gap-2 mb-4">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                      <Server className="w-5 h-5 text-red-600" />
                    </div>
                    <h4 className="text-base font-semibold text-gray-900 truncate">
                      {health.service_display_name || health.service_name}
                    </h4>
                  </div>
                  <div className="flex-shrink-0">
                    <StatusBadge {...getHealthStatusForBadge(health.status)} />
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Response Time:</span>
                    <span className="text-gray-900 font-medium">{health.response_time_ms ? `${health.response_time_ms}ms` : 'N/A'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Last Check:</span>
                    <span className="text-gray-900 font-medium">{formatDateTime(health.checked_at)}</span>
                  </div>
                  {health.error_message && (
                    <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-red-700 text-xs">{health.error_message}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Server className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-gray-900 mb-1">
              {errors.health ? 'Live Health Checks Unavailable' : 'No Health Check Data'}
            </p>
            <p className="text-sm text-gray-500">
              {errors.health
                ? 'The portal will retry automatically. No healthy state is being assumed.'
                : 'Health check data will appear here once available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAlerts = () => (
    <div className="bg-white rounded-2xl shadow-sm border">
      <div className="px-6 py-4 border-b">
        <h3 className="text-lg font-semibold text-gray-900">System Alerts</h3>
        <p className="text-sm text-gray-500 mt-1">
          Showing the latest {systemAlerts.length} records · {metrics.activeAlerts} active in total
        </p>
      </div>
      <div className="p-6">
        {systemAlerts.length > 0 ? (
          <div className="space-y-4">
            {systemAlerts.map((alert) => (
              <div key={alert.id || `${alert.title}-${alert.created_at}`} className={`border rounded-xl p-6 transition-all duration-200 ${
                alert.resolved 
                  ? 'bg-gray-50 border-gray-200' 
                  : 'bg-white border-orange-200 hover:shadow-md'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        alert.resolved ? 'bg-gray-100' : 'bg-orange-100'
                      }`}>
                        <Bell className={`w-5 h-5 ${alert.resolved ? 'text-gray-400' : 'text-orange-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-base font-semibold text-gray-900 truncate">{alert.title}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`px-3 py-1 text-xs font-medium rounded-full ${getSeverityColor(alert.severity)}`}>
                            {alert.severity}
                          </span>
                          {alert.resolved && (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                              ✅ Resolved
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed">{alert.message}</p>
                    {alert.action_required && (
                      <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-4 rounded-lg mb-4">
                        <div className="flex items-start space-x-2">
                          <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <strong className="font-semibold">Action Required:</strong>
                            <p className="mt-1">{alert.action_required}</p>
                          </div>
                        </div>
                      </div>
                    )}
                    {alert.related_task_id && (
                      <div className="mb-4">
                        <button
                          onClick={() => {
                            // Find the related task and open it
                            const relatedTask = taskExecutions.find(t => t.task_id === alert.related_task_id);
                            if (relatedTask) {
                              openTaskDetails(relatedTask);
                            } else {
                              toast.error('Related task not found in current data');
                              setActiveTab('tasks');
                            }
                          }}
                          className="inline-flex items-center px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          <Clock className="w-4 h-4 mr-2" />
                          View Related Task: {alert.related_task_name || alert.related_task_id.substring(0, 8)}
                          <ExternalLink className="w-3 h-3 ml-2" />
                        </button>
                      </div>
                    )}
                    <div className="text-xs text-gray-500 space-y-1">
                      <p>Created: {formatDateTime(alert.created_at)}</p>
                      {alert.resolved_at && (
                        <p>Resolved: {formatDateTime(alert.resolved_at)}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              errors.alerts ? 'bg-orange-100' : 'bg-green-100'
            }`}>
              {errors.alerts ? (
                <AlertTriangle className="w-8 h-8 text-orange-600" />
              ) : (
                <CheckCircle className="w-8 h-8 text-green-600" />
              )}
            </div>
            <p className="text-gray-900 text-lg font-medium">
              {errors.alerts ? 'System alerts are unavailable' : 'No system alerts'}
            </p>
            <p className="text-gray-500 text-sm mt-1">
              {errors.alerts
                ? 'The portal will retry automatically. No clear state is being assumed.'
                : 'No active or resolved alerts were returned.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderAudit = () => (
    <div className="bg-white rounded-2xl shadow-sm border">
      {/* Search Toolbar - Design System Pattern */}
      <div className="px-6 py-4 border-b bg-gray-50 rounded-t-2xl">
        {/* Search + Filter Toggle */}
        <div className="flex items-center justify-between gap-3">
          <DebouncedSearchInput
            value={auditSearch}
            onSearch={(value) => {
              setAuditSearch(value);
              setAuditPage(1);
            }}
            placeholder="Search audit logs..."
            className="max-w-xl"
          />
          <div className="flex items-center gap-2">
            <FilterButton
              theme="admin"
              isOpen={showFilters}
              activeCount={[auditFilters.staffId, auditFilters.actionType, auditFilters.modelName, auditFilters.startDate, auditFilters.endDate].filter(Boolean).length}
              onClick={() => setShowFilters(v => !v)}
            />
          </div>
        </div>
        
        {/* Collapsible Filter Panel */}
        {showFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="form-group">
              <label className="form-label text-xs text-gray-600">Staff ID</label>
              <select
                className="select-admin w-full"
                value={auditFilters.staffId}
                onChange={(e) => {
                  setAuditFilters({...auditFilters, staffId: e.target.value});
                  setAuditPage(1);
                }}
              >
                <option value="">All Staff</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label text-xs text-gray-600">Action</label>
              <select
                className="select-admin w-full"
                value={auditFilters.actionType}
                onChange={(e) => {
                  setAuditFilters({...auditFilters, actionType: e.target.value});
                  setAuditPage(1);
                }}
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label text-xs text-gray-600">Model</label>
              <select
                className="select-admin w-full"
                value={auditFilters.modelName}
                onChange={(e) => {
                  setAuditFilters({...auditFilters, modelName: e.target.value});
                  setAuditPage(1);
                }}
              >
                <option value="">All Models</option>
                <option value="LeaveBalance">Leave Balance</option>
                <option value="LeaveApplication">Leave Application</option>
                <option value="StaffUser">Staff User</option>
              </select>
            </div>
            
            <div className="form-group">
              <label className="form-label text-xs text-gray-600">Date Range</label>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="input-admin w-full"
                  value={auditFilters.startDate}
                  onChange={(e) => {
                    setAuditFilters({...auditFilters, startDate: e.target.value});
                    setAuditPage(1);
                  }}
                />
                <input
                  type="date"
                  className="input-admin w-full"
                  value={auditFilters.endDate}
                  onChange={(e) => {
                    setAuditFilters({...auditFilters, endDate: e.target.value});
                    setAuditPage(1);
                  }}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Active Filters Chips */}
        {(auditSearch || auditFilters.staffId || auditFilters.actionType || auditFilters.modelName) && (
          <div className="mt-3 flex flex-wrap gap-2">
            {auditSearch && (
              <span className="pill pill-secondary inline-flex items-center">
                Search: {auditSearch}
                <button onClick={() => setAuditSearch('')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            <button
              onClick={() => {
                setAuditSearch('');
                setAuditFilters({staffId: '', actionType: '', modelName: '', startDate: '', endDate: ''});
                setAuditPage(1);
              }}
              className="btn btn-outline btn-sm"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Table Header */}
      <div className="px-6 py-4 border-b flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Audit Trail</h3>
          <p className="text-sm text-gray-500 mt-1">User activity and system changes</p>
        </div>
        {auditRefreshing && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-500" role="status">
            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            Refreshing
          </span>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">User</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Action</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Model</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {auditLoading ? (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <RefreshCw className="w-6 h-6 text-red-600 animate-spin mx-auto" />
                  <p className="text-sm text-gray-500 mt-3">Loading audit logs…</p>
                </td>
              </tr>
            ) : auditLogs.length > 0 ? auditLogs.map((log) => (
              <tr key={log.id} className="table-row min-h-[44px]">
                <td className="px-6 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {formatDateTime(log.audit_timestamp)}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-gray-900">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600">
                        {(log.audit_staff_id || 'SYS').charAt(0)}
                      </span>
                    </div>
                    <span className="font-medium">{log.audit_staff_id || 'System'}</span>
                  </div>
                </td>
                <td className="px-6 py-3">
                  <StatusBadge
                    status={
                      log.audit_action_type === 'CREATE' ? 'approved' :
                      log.audit_action_type === 'UPDATE' ? 'in_progress' :
                      log.audit_action_type === 'DELETE' ? 'rejected' :
                      'draft'
                    }
                    label={log.audit_action_type}
                  />
                </td>
                <td className="px-6 py-3 text-sm font-medium text-gray-900">
                  {log.audit_model_name}
                </td>
                <td className="px-6 py-3">
                  <button
                    onClick={() => {
                      setSelectedAuditLog(log);
                      setShowAuditModal(true);
                    }}
                    className="icon-button-admin"
                    title="View Details"
                    aria-label="View audit log details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="5" className="px-6 py-12 text-center">
                  <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-base font-semibold text-gray-900">
                    {auditError ? 'Audit logs are unavailable' : 'No audit logs found'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {auditError
                      ? 'The portal will retry automatically while this tab remains open.'
                      : 'Try adjusting the current search or filters.'}
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {auditCount > 0 && (
        <Pagination
          currentPage={auditPage}
          totalPages={Math.ceil(auditCount / auditPageSize)}
          onPageChange={setAuditPage}
          totalCount={auditCount}
          pageSize={auditPageSize}
          theme="admin"
          noun="results"
        />
      )}
      {Math.ceil(auditCount / auditPageSize) > 1 && (
        <div className="flex items-center justify-center gap-2 -mt-2 pb-4">
          <span className="text-xs text-gray-600">Rows per page:</span>
          <select
            className="select-admin"
            value={auditPageSize}
            onChange={(e) => {
              setAuditPageSize(Number(e.target.value));
              setAuditPage(1);
            }}
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
      )}
    </div>
  );

  const failedSources = [
    errors.tasks ? 'task executions' : null,
    errors.health ? 'live health checks' : null,
    errors.alerts ? 'system alerts' : null,
    activeTab === 'audit' && auditError ? 'audit logs' : null,
  ].filter(Boolean);
  const refreshing = isRefreshing || (activeTab === 'audit' && auditRefreshing);

  if (loading) {
    return (
      <div className="w-full space-y-8">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 bg-gray-200 rounded w-80 animate-pulse"></div>
            <div className="h-5 bg-gray-200 rounded w-96 animate-pulse"></div>
          </div>
          <div className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
        </div>

        {/* Tab Navigation Skeleton */}
        <div className="flex items-center gap-2 flex-wrap mb-4 md:mb-6">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-10 bg-gray-200 rounded-full w-32 animate-pulse"></div>
          ))}
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
              <div className="flex items-center justify-between animate-pulse">
                <div className="space-y-2 min-w-0">
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="w-9 h-9 sm:w-12 sm:h-12 bg-gray-200 rounded-full flex-shrink-0"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Content Area Skeleton */}
        <div className="bg-white rounded-2xl shadow-sm border">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-48"></div>
              <div className="flex items-center gap-2">
                <div className="h-10 bg-gray-200 rounded w-64"></div>
                <div className="h-10 bg-gray-200 rounded w-10"></div>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-40"></div>
                    <div className="h-3 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-6 bg-gray-200 rounded-full w-20"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-1 min-w-0">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <Server className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <h1 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate">System Monitoring & Audit Center</h1>
          </div>
          <p className="text-xs sm:text-sm text-gray-500 ml-11 sm:ml-12">Monitor automated tasks, system health, and user activity</p>
        </div>
        <div className="flex items-center gap-3 self-start sm:self-auto">
          <div className="text-left sm:text-right" role="status" aria-live="polite">
            <p className="flex items-center sm:justify-end gap-1.5 text-xs font-medium text-gray-700">
              <span className={`w-2 h-2 rounded-full ${
                failedSources.length > 0
                  ? 'bg-orange-500'
                  : refreshing
                    ? 'bg-blue-500 animate-pulse'
                    : 'bg-green-500'
              }`} />
              {refreshing
                ? 'Refreshing live data…'
                : lastUpdated
                  ? `Updated ${formatRelativeTime(lastUpdated)}`
                  : 'Waiting for live data'}
            </p>
            <p className="hidden sm:block text-xs text-gray-500 mt-0.5">
              Auto-refreshes every 30 seconds
            </p>
          </div>
          <IconButton
            icon={RefreshCw}
            onClick={handleRefresh}
            loading={refreshing}
            variant="primary"
            title="Refresh monitoring data"
            ariaLabel="Refresh monitoring data"
          />
        </div>
      </div>

      {failedSources.length > 0 && (
        <div
          className="flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-4"
          role="alert"
        >
          <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-orange-600" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-orange-900">Some live data could not refresh</p>
            <p className="text-sm text-orange-800 mt-1">
              Last successful values remain visible for {failedSources.join(', ')}. The portal will retry automatically.
            </p>
          </div>
        </div>
      )}

      {/* Modern Pill Tabs - Admin Theme */}
      <div className="flex items-center gap-2 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth mb-4 md:mb-6">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`tab-btn-admin ${activeTab === tab.id ? 'active' : ''}`}
              title={tab.description}
            >
              <Icon className="w-4 h-4" />
              {tab.name}
              {tab.badge && tab.badge > 0 && (
                <CountBadge count={tab.badge} theme="admin" active={activeTab === tab.id} className="ml-1" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'tasks' && renderTasks()}
        {activeTab === 'health' && renderHealth()}
        {activeTab === 'alerts' && renderAlerts()}
        {activeTab === 'audit' && renderAudit()}
      </div>

      {showTaskModal && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={closeTaskDetails} />
            <div className="relative bg-white w-full max-w-5xl mx-4 rounded-2xl shadow-xl border border-gray-200">
              <div className="px-6 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="modal-title">Task Details</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {(selectedTask?.task_display_name || selectedTask?.task_name?.replace('staff_core.tasks.', '').replace(/_/g, ' '))} · ID: {selectedTask?.task_id}
                  </p>
                </div>
                <button className="btn btn-admin-secondary" onClick={closeTaskDetails}>Close</button>
              </div>
              <div className="p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-xs text-gray-500">Status</p>
                    <p className="text-sm font-semibold text-gray-900">{selectedTask?.task_status}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-xs text-gray-500">Started</p>
                    <p className="text-sm font-semibold text-gray-900">{formatDateTime(selectedTask?.started_at)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-xs text-gray-500">Duration</p>
                    <p className="text-sm font-semibold text-gray-900">{formatDuration(selectedTask?.duration_seconds)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 border">
                    <p className="text-xs text-gray-500">Result</p>
                    <p className="text-sm font-semibold text-gray-900 truncate">{selectedTask?.result_summary || 'No summary available'}</p>
                  </div>
                </div>

                <div className="border rounded-2xl">
                  <div className="px-6 py-4 border-b flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-gray-900">Per-Employee Effects</h4>
                      <p className="text-xs text-gray-500">{taskEffects.count} record(s)</p>
                    </div>
                  </div>
                  <div>
                    {taskEffects.loading ? (
                      <div className="p-6 text-sm text-gray-500">Loading effects…</div>
                    ) : (taskEffects.rows && taskEffects.rows.length > 0 ? (
                      (() => {
                        // Determine table type based on task name or first row's metadata
                        const taskName = selectedTask?.task_name || '';
                        const firstRow = taskEffects.rows[0];
                        const metadata = firstRow?.metadata || {};
                        
                        // Employment status change tasks
                        if (taskName.includes('lapsed_employment_status') || metadata.old_employment_status) {
                          return (
                            <>
                            <EffectsMobileCards
                              rows={taskEffects.rows}
                              renderBadge={(row) => (
                                <StatusBadge status={row.metadata?.new_employment_status || 'draft'} label={row.metadata?.new_employment_status || '-'} />
                              )}
                              fields={[
                                { label: 'Old Status', render: (row) => <StatusBadge status={row.metadata?.old_employment_status || 'draft'} label={row.metadata?.old_employment_status || '-'} /> },
                                { label: 'End Date', render: (row) => row.metadata?.employment_end_date || '-' },
                              ]}
                            />
                            <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Staff ID</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Old Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">New Status</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">End Date</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {taskEffects.rows.map((row, idx) => (
                                  <tr key={idx}>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_id_display || row.staff_id}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">
                                      <StatusBadge status={row.metadata?.old_employment_status || 'draft'} label={row.metadata?.old_employment_status || '-'} />
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-900">
                                      <StatusBadge status={row.metadata?.new_employment_status || 'draft'} label={row.metadata?.new_employment_status || '-'} />
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.metadata?.employment_end_date || '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            </>
                          );
                        }
                        
                        // Overtime aggregation tasks
                        if (taskName.includes('overtime') || metadata.old_overtime_hours !== undefined) {
                          return (
                            <>
                            <EffectsMobileCards
                              rows={taskEffects.rows}
                              renderBadge={(row) => (
                                <StatusBadge
                                  status={
                                    row.metadata?.new_compliance_status === 'compliant' ? 'on_track' :
                                    row.metadata?.new_compliance_status === 'warning' ? 'watch' :
                                    row.metadata?.new_compliance_status === 'exceeded' ? 'exceeded' :
                                    'draft'
                                  }
                                  label={row.metadata?.new_compliance_status || '-'}
                                />
                              )}
                              fields={[
                                { label: 'Old OT Hours', numeric: true, render: (row) => row.metadata?.old_overtime_hours?.toFixed(1) ?? '-' },
                                { label: 'New OT Hours', numeric: true, render: (row) => row.metadata?.new_overtime_hours?.toFixed(1) ?? '-' },
                                {
                                  label: 'Delta',
                                  numeric: true,
                                  render: (row) => row.metadata?.delta_overtime_hours !== undefined ? (
                                    <span className={row.metadata.delta_overtime_hours > 0 ? 'text-orange-600' : 'text-green-600'}>
                                      {row.metadata.delta_overtime_hours > 0 ? '+' : ''}{row.metadata.delta_overtime_hours.toFixed(1)}
                                    </span>
                                  ) : '-',
                                },
                              ]}
                            />
                            <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Staff ID</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Old OT Hours</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">New OT Hours</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Delta</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Compliance</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {taskEffects.rows.map((row, idx) => (
                                  <tr key={idx}>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_id_display || row.staff_id}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.metadata?.old_overtime_hours?.toFixed(1) ?? '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.metadata?.new_overtime_hours?.toFixed(1) ?? '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">
                                      {row.metadata?.delta_overtime_hours !== undefined ? (
                                        <span className={row.metadata.delta_overtime_hours > 0 ? 'text-orange-600' : 'text-green-600'}>
                                          {row.metadata.delta_overtime_hours > 0 ? '+' : ''}{row.metadata.delta_overtime_hours.toFixed(1)}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-900">
                                      <StatusBadge
                                        status={
                                          row.metadata?.new_compliance_status === 'compliant' ? 'on_track' :
                                          row.metadata?.new_compliance_status === 'warning' ? 'watch' :
                                          row.metadata?.new_compliance_status === 'exceeded' ? 'exceeded' :
                                          'draft'
                                        }
                                        label={row.metadata?.new_compliance_status || '-'}
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            </>
                          );
                        }
                        
                        // Carry forward tasks
                        if (taskName.includes('carry_forward') || metadata.action === 'carry_forward') {
                          return (
                            <>
                            <EffectsMobileCards
                              rows={taskEffects.rows}
                              fields={[
                                { label: 'Leave Type', render: (row) => row.leave_type_name || '-' },
                                { label: 'Unused Days', numeric: true, render: (row) => row.metadata?.unused_days ?? '-' },
                                {
                                  label: 'Carried Forward',
                                  numeric: true,
                                  render: (row) => (
                                    <span className="font-semibold text-green-600">+{row.metadata?.carried_forward_days ?? '-'}</span>
                                  ),
                                },
                                { label: 'Max Allowed', numeric: true, render: (row) => row.metadata?.max_carry_forward ?? 'No limit' },
                              ]}
                            />
                            <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Staff ID</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Leave Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Unused Days</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Carried Forward</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Max Allowed</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {taskEffects.rows.map((row, idx) => (
                                  <tr key={idx}>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_id_display || row.staff_id}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.leave_type_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.metadata?.unused_days ?? '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900 font-semibold text-green-600">
                                      +{row.metadata?.carried_forward_days ?? '-'}
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.metadata?.max_carry_forward ?? 'No limit'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            </>
                          );
                        }
                        
                        // Balance creation/materialization tasks
                        if (taskName.includes('materialize') || metadata.action === 'balance_created') {
                          return (
                            <>
                            <EffectsMobileCards
                              rows={taskEffects.rows}
                              renderBadge={() => <StatusBadge status="completed" label="Created" />}
                              fields={[
                                { label: 'Leave Type', render: (row) => row.leave_type_name || row.metadata?.leave_type_name || '-' },
                                { label: 'Reason', render: (row) => row.metadata?.eligibility_reason || '-' },
                              ]}
                            />
                            <div className="hidden md:block overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Staff ID</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Leave Type</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Action</th>
                                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Reason</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {taskEffects.rows.map((row, idx) => (
                                  <tr key={idx}>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_id_display || row.staff_id}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.staff_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">{row.leave_type_name || row.metadata?.leave_type_name || '-'}</td>
                                    <td className="px-6 py-3 text-sm text-gray-900">
                                      <StatusBadge status="completed" label="Created" />
                                    </td>
                                    <td className="px-6 py-3 text-sm text-gray-900 max-w-xs truncate" title={row.metadata?.eligibility_reason}>
                                      {row.metadata?.eligibility_reason || '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>
                            </>
                          );
                        }
                        
                        // Default: Leave balance effects (annual leave accrual, etc.)
                        return (
                          <>
                          <EffectsMobileCards
                            rows={taskEffects.rows}
                            fields={[
                              { label: 'Before Accrued', numeric: true, render: (row) => row.before_accrued_days != null ? formatLeaveDays(row.before_accrued_days) : '-' },
                              { label: 'Delta', numeric: true, render: (row) => row.delta_accrued_days != null ? formatLeaveDays(row.delta_accrued_days) : '-' },
                              { label: 'After Accrued', numeric: true, render: (row) => row.after_accrued_days != null ? formatLeaveDays(row.after_accrued_days) : '-' },
                              { label: 'Before Remaining', numeric: true, render: (row) => row.before_remaining_days != null ? formatLeaveDays(row.before_remaining_days) : '-' },
                              { label: 'After Remaining', numeric: true, render: (row) => row.after_remaining_days != null ? formatLeaveDays(row.after_remaining_days) : '-' },
                            ]}
                          />
                          <div className="hidden md:block overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Staff ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Before Accrued</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Delta</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">After Accrued</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Before Remaining</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">After Remaining</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {taskEffects.rows.map((row, idx) => (
                                <tr key={idx}>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.staff_id_display || row.staff_id}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.staff_name || '-'}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.before_accrued_days != null ? formatLeaveDays(row.before_accrued_days) : '-'}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.delta_accrued_days != null ? formatLeaveDays(row.delta_accrued_days) : '-'}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.after_accrued_days != null ? formatLeaveDays(row.after_accrued_days) : '-'}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.before_remaining_days != null ? formatLeaveDays(row.before_remaining_days) : '-'}</td>
                                  <td className="px-6 py-3 text-sm text-gray-900">{row.after_remaining_days != null ? formatLeaveDays(row.after_remaining_days) : '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          </div>
                          </>
                        );
                      })()
                    ) : (
                      <div className="p-6 text-sm text-gray-500">No per-employee effects recorded for this task.</div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Portal>
      )}

      {/* Audit Details Modal */}
      <AuditDetailsModal
        isOpen={showAuditModal}
        auditLog={selectedAuditLog}
        onClose={() => setShowAuditModal(false)}
        onPrevious={() => navigateAuditLog('prev')}
        onNext={() => navigateAuditLog('next')}
      />
    </div>
  );
};

export default SystemMonitoring;
